// Shared 429 ("Too many attempts") handling for the public auth forms
// (login, register, forgot password). Never auto-retries: it only tells the
// form how long to keep its submit button disabled.

const DEFAULT_RETRY_SECONDS = 10;

export function isRateLimited(error) {
  return !!(error && error.response && error.response.status === 429);
}

// Seconds to wait, from the Retry-After header (seconds or HTTP date), falling
// back to the number in message[0] ("... try again in 10 seconds.").
export function getRetryAfterSeconds(error) {
  const response = error && error.response;
  if (!response) return DEFAULT_RETRY_SECONDS;

  const header = response.headers && response.headers["retry-after"];
  if (header !== undefined && header !== null && header !== "") {
    const seconds = Number(header);
    if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);
    const date = Date.parse(header);
    if (!Number.isNaN(date)) return Math.max(1, Math.ceil((date - Date.now()) / 1000));
  }

  const match = /(\d+)\s*sec/i.exec(firstMessage(response.data) || "");
  if (match) return parseInt(match[1], 10);

  return DEFAULT_RETRY_SECONDS;
}

// API messages arrive as ["..."] or occasionally a plain string.
export function firstMessage(data) {
  if (!data) return "";
  const message = data.message;
  if (Array.isArray(message)) return message[0] || "";
  return typeof message === "string" ? message : "";
}

// Options API mixin: call `handleRateLimit(err)` in a catch block; it returns
// true when the error was a 429 and starts a live countdown. Bind the submit
// button's :disabled to `rateLimited` and show `rateLimitText`.
export const rateLimitMixin = {
  data() {
    return {
      rateLimitSeconds: 0,
      rateLimitTimer: null,
    };
  },
  computed: {
    rateLimited() {
      return this.rateLimitSeconds > 0;
    },
    rateLimitText() {
      return this.rateLimited ? `Too many attempts. Try again in ${this.rateLimitSeconds}s…` : "";
    },
  },
  methods: {
    handleRateLimit(error) {
      if (!isRateLimited(error)) return false;
      this.startRateLimitCountdown(getRetryAfterSeconds(error));
      return true;
    },
    startRateLimitCountdown(seconds) {
      this.clearRateLimitCountdown();
      const endsAt = Date.now() + seconds * 1000;
      const tick = () => {
        this.rateLimitSeconds = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
        if (this.rateLimitSeconds === 0) this.clearRateLimitCountdown();
      };
      tick();
      if (this.rateLimitSeconds > 0) this.rateLimitTimer = setInterval(tick, 250);
    },
    clearRateLimitCountdown() {
      if (this.rateLimitTimer) clearInterval(this.rateLimitTimer);
      this.rateLimitTimer = null;
    },
  },
  beforeUnmount() {
    this.clearRateLimitCountdown();
  },
};
